/**
 * Alert runtime — storage, de-duplication and delivery.
 *
 * Two behaviours matter more than the rest here. First, a rule fires on a *record* once: matches
 * are keyed by (rule, record) and remembered, so a poll every 30 seconds does not re-alert the same
 * opportunity forever. Second, delivery degrades quietly — a blocked autoplay or a denied
 * notification permission must never break the view that is evaluating rules.
 */

import { AlertRule } from './alertRules';

const RULES_KEY = 'mmecAlertRules';
const LOG_KEY = 'mmecAlertLog';
/** Alerts kept for review. Enough to cover a session without unbounded growth. */
const LOG_LIMIT = 300;
const SEEN_KEY = 'mmecAlertSeen';
/** Cap on remembered (rule, record) pairs so storage cannot grow without bound. */
const SEEN_LIMIT = 4000;

export interface AlertHistoricalTarget {
  underlying: string;
  strike: number | string;
  expiry?: string;
  optionType?: 'CALL' | 'PUT';
}

export interface AlertEvent {
  id: string;
  ruleId: string;
  ruleName: string;
  sourceId: string;
  sourceLabel?: string;
  recordKey: string;
  summary: string;
  /** The conditions that matched, with the record's own values. */
  details?: string[];
  /** Present when the record is a single contract, so the alert can open its history. */
  historical?: AlertHistoricalTarget;
  at: number;
}

// ── rule storage ──────────────────────────────────────────────────────────────

export function loadRules(): AlertRule[] {
  try {
    const raw = window.localStorage.getItem(RULES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((r) => r && typeof r.id === 'string' && r.root);
  } catch {
    return [];
  }
}

export function saveRules(rules: AlertRule[]): void {
  try {
    window.localStorage.setItem(RULES_KEY, JSON.stringify(rules));
  } catch {
    // Storage unavailable: rules stay for this session only.
  }
}

// ── seen-record memory ────────────────────────────────────────────────────────

type SeenMap = Record<string, number>;

function loadSeen(): SeenMap {
  try {
    const raw = window.sessionStorage.getItem(SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as SeenMap) : {};
  } catch {
    return {};
  }
}

let seen: SeenMap = typeof window === 'undefined' ? {} : loadSeen();

function persistSeen(): void {
  try {
    const entries = Object.entries(seen);
    if (entries.length > SEEN_LIMIT) {
      // Drop the oldest half rather than clearing: recent matches are the ones worth suppressing.
      entries.sort((a, b) => b[1] - a[1]);
      seen = Object.fromEntries(entries.slice(0, Math.floor(SEEN_LIMIT / 2)));
    }
    window.sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    /* best effort */
  }
}

export function hasFired(ruleId: string, recordKey: string): boolean {
  return Boolean(seen[`${ruleId}|${recordKey}`]);
}

export function markFired(ruleId: string, recordKey: string): void {
  seen[`${ruleId}|${recordKey}`] = Date.now();
  persistSeen();
}

/** Forget a rule's history, so editing a rule lets it alert on current data again. */
export function resetRuleHistory(ruleId: string): void {
  const prefix = `${ruleId}|`;
  let changed = false;
  for (const key of Object.keys(seen)) {
    if (key.startsWith(prefix)) {
      delete seen[key];
      changed = true;
    }
  }
  if (changed) persistSeen();
}

// ── throttling ────────────────────────────────────────────────────────────────

const lastFiredAt = new Map<string, number>();

export function withinThrottle(rule: AlertRule): boolean {
  const last = lastFiredAt.get(rule.id);
  if (!last) return false;
  return Date.now() - last < Math.max(0, rule.throttleSec) * 1000;
}

export function noteFired(rule: AlertRule): void {
  lastFiredAt.set(rule.id, Date.now());
}

// ── delivery ──────────────────────────────────────────────────────────────────

let audio: HTMLAudioElement | null = null;

export function playAlertSound(file?: string): void {
  try {
    const src = file || '/bell-notification-337658.mp3';
    if (!audio) audio = new Audio(src);
    else if (!audio.src.endsWith(src)) audio.src = src;
    audio.currentTime = 0;
    // Browsers block autoplay until the user has interacted; failing here is expected, not an error.
    void audio.play().catch(() => undefined);
  } catch {
    /* sound is a nicety, never a requirement */
  }
}

export function requestBrowserPermission(): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') void Notification.requestPermission();
  } catch {
    /* ignore */
  }
}

export function showBrowserNotification(title: string, body: string): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const n = new Notification(title, { body, icon: '/logo.png' });
    n.onclick = () => {
      try {
        window.focus();
        n.close();
      } catch {
        /* ignore */
      }
    };
  } catch {
    /* ignore */
  }
}

// ── in-app event bus (drives the popup) ───────────────────────────────────────

type Listener = (event: AlertEvent) => void;
const listeners = new Set<Listener>();

export function onAlert(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitAlert(event: AlertEvent): void {
  listeners.forEach((l) => {
    try {
      l(event);
    } catch {
      /* a broken listener must not stop the others */
    }
  });
}

/** Deliver one alert through whichever channels the rule enables. */
export function deliver(rule: AlertRule, event: AlertEvent): void {
  // Logged regardless of channel: an alert you missed is exactly the one worth reviewing.
  appendAlertLog(event);
  if (rule.actions.sound) playAlertSound(rule.actions.soundFile);
  if (rule.actions.browser) {
    const body = [event.summary, ...(event.details ?? [])].filter(Boolean).join(' — ');
    showBrowserNotification(rule.name, body || 'Alert triggered');
  }
  if (rule.actions.popup) emitAlert(event);
}


// ── alert log ─────────────────────────────────────────────────────────────────
// Notifications are transient by nature; a rule that fired while nobody was looking is still
// worth seeing. Everything delivered is appended here, newest first, and survives a reload.

const LOG_EVENT = 'mmec-alert-log-changed';
const SEEN_AT_KEY = 'mmecAlertsSeenAt';

export function loadAlertLog(): AlertEvent[] {
  try {
    const raw = window.localStorage.getItem(LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AlertEvent[]) : [];
  } catch {
    return [];
  }
}

export function appendAlertLog(event: AlertEvent): void {
  try {
    const next = [event, ...loadAlertLog()].slice(0, LOG_LIMIT);
    window.localStorage.setItem(LOG_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(LOG_EVENT, { detail: next }));
  } catch {
    /* the popup still fired; losing the log entry is not worth breaking delivery */
  }
}

export function clearAlertLog(): void {
  try {
    window.localStorage.removeItem(LOG_KEY);
    window.dispatchEvent(new CustomEvent(LOG_EVENT, { detail: [] }));
  } catch {
    /* ignore */
  }
}

export function onAlertLogChange(listener: (events: AlertEvent[]) => void): () => void {
  const h = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    listener(Array.isArray(detail) ? detail : loadAlertLog());
  };
  window.addEventListener(LOG_EVENT, h);
  return () => window.removeEventListener(LOG_EVENT, h);
}


/**
 * Unread count for the bell: alerts logged since the pane was last opened. Kept as a timestamp
 * rather than a counter so it stays correct across tabs and reloads without anything to reconcile.
 */
export function alertsSeenAt(): number {
  try {
    return Number(window.localStorage.getItem(SEEN_AT_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function markAlertsSeen(): void {
  try {
    window.localStorage.setItem(SEEN_AT_KEY, String(Date.now()));
    window.dispatchEvent(new CustomEvent(LOG_EVENT, { detail: loadAlertLog() }));
  } catch {
    /* ignore */
  }
}

export function unreadAlertCount(events?: AlertEvent[]): number {
  const since = alertsSeenAt();
  return (events ?? loadAlertLog()).filter((e) => e.at > since).length;
}
